/**
 * Services Context
 * 
 * Provides dependency injection for all services to React components.
 * This eliminates singleton imports and makes testing easier.
 * 
 * Usage in production:
 * ```tsx
 * <ServicesProvider>
 *   <App />
 * </ServicesProvider>
 * ```
 * 
 * Usage in tests:
 * ```tsx
 * <ServicesProvider services={testHost.services}>
 *   <ComponentUnderTest />
 * </ServicesProvider>
 * ```
 * 
 * Usage in components:
 * ```tsx
 * const { serverSync, hydrationCoordinator } = useServices()
 * ```
 */

import { createContext, useContext, type ReactNode } from 'react'
import { serverSyncService, type ServerSyncService } from '../infrastructure/server-sync-service'
import { scenarioHydrationCoordinator, type ScenarioHydrationCoordinator } from '../coordination/scenario-hydration-coordinator'
import { memoryStateService } from '../state/memory-store'
import { connectionStateService } from '../infrastructure/connection-state-service'

export interface Services {
  serverSync: ServerSyncService
  hydrationCoordinator: ScenarioHydrationCoordinator
  memoryState: typeof memoryStateService
  connectionState: typeof connectionStateService
}

const ServicesContext = createContext<Services | null>(null)

interface ServicesProviderProps {
  children: ReactNode
  services?: Services
}

/**
 * Provider component that supplies services to the component tree
 */
export function ServicesProvider({ children, services }: ServicesProviderProps) {
  // Use provided services (for tests) or default singletons (for production)
  const value: Services = services ?? {
    serverSync: serverSyncService,
    hydrationCoordinator: scenarioHydrationCoordinator,
    memoryState: memoryStateService,
    connectionState: connectionStateService
  }

  return (
    <ServicesContext.Provider value={value}>
      {children}
    </ServicesContext.Provider>
  )
}

/**
 * Hook to access services from any component
 * @throws Error if used outside ServicesProvider
 */
export function useServices(): Services {
  const context = useContext(ServicesContext)
  if (!context) {
    throw new Error('useServices must be used within ServicesProvider')
  }
  return context
}
